import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";
import EditQuoteModal from "../components/EditQuoteModal";
import ReviseEmiTermsModal from "../components/ReviseEmiTermsModal";
import { UI, btnSx } from "../components/ActionDrawer";
import { Empty, PageHead, SectionTitle, Tag } from "../components/DashTable";

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const timeAgo = (date) => {
  if (!date) return "";
  const diffMs = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

/* Left edge stripe, same language as the action queue. */
const STRIPE = {
  quotation: UI.amber,
  advance: UI.blue,
  emi: UI.red,
};

/** One row in the action list: stripe, title, meta line, action button. */
const ActionRow = ({ kind, title, tag, meta, isLast, action }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "5px 1fr", sm: "5px 1fr auto" },
      borderBottom: isLast ? 0 : "1px solid",
      borderBottomColor: UI.line,
    }}
  >
    <Box sx={{ bgcolor: STRIPE[kind] || UI.line }} />

    <Box sx={{ padding: "14px 18px", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          marginBottom: "5px",
        }}
      >
        <Box sx={{ fontSize: 14, fontWeight: 500, color: UI.ink }}>{title}</Box>
        {tag}
      </Box>
      <Box sx={{ fontSize: 12, color: UI.ink3, lineHeight: 1.6 }}>{meta}</Box>
    </Box>

    <Box
      sx={{
        padding: { xs: "0 18px 14px", sm: "14px 18px" },
        gridColumn: { xs: 2, sm: "auto" },
        display: "flex",
        alignItems: "center",
        whiteSpace: "nowrap",
      }}
    >
      {action}
    </Box>
  </Box>
);

/** Read only figure block used inside the drawers. */
const Stat = ({ label, value }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      bgcolor: UI.bg2,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "6px",
      padding: "10px 12px",
    }}
  >
    <Box sx={{ fontSize: 11, color: UI.ink3 }}>{label}</Box>
    <Box sx={{ fontSize: 14, fontWeight: 500, color: UI.ink, marginTop: "2px" }}>
      {value}
    </Box>
  </Box>
);

const Banner = ({ tone = "i", children }) => {
  const tones = {
    i: { bg: UI.blueBg, line: UI.blue, fg: UI.blueInk },
    g: { bg: UI.greenBg, line: UI.green, fg: UI.greenInk },
  };
  const t = tones[tone] || tones.i;
  return (
    <Box
      sx={{
        bgcolor: t.bg,
        border: "1px solid",
        borderColor: t.line,
        color: t.fg,
        borderRadius: "4px",
        padding: "10px 13px",
        fontSize: 12,
        marginBottom: "18px",
      }}
    >
      {children}
    </Box>
  );
};

const PAGE_SIZE = 15;

const NeedsAction = () => {
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [quotationItems, setQuotationItems] = useState([]);
  const [advanceItems, setAdvanceItems] = useState([]);
  const [emiItems, setEmiItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // These are three logically distinct queues (different filters, different
  // actions) rendered together for convenience — each keeps its own page and
  // pager rather than sharing one across unrelated lists.
  const [quotationPage, setQuotationPage] = useState(1);
  const [advancePage, setAdvancePage] = useState(1);
  const [emiPage, setEmiPage] = useState(1);
  const [quotationPagination, setQuotationPagination] = useState(null);
  const [advancePagination, setAdvancePagination] = useState(null);
  const [emiPagination, setEmiPagination] = useState(null);

  // Advance payment link modal state
  const [selectedAdvanceItem, setSelectedAdvanceItem] = useState(null);
  const [advanceForm, setAdvanceForm] = useState({
    amount: "",
    dueDate: "",
    paymentMode: "razorpay",
  });
  const [submittingAdvance, setSubmittingAdvance] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  // Quotation review panel state
  const [selectedQuoteItem, setSelectedQuoteItem] = useState(null);
  const [note, setNote] = useState("");
  const [pricingChecked, setPricingChecked] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [editQuoteOrderId, setEditQuoteOrderId] = useState(null);

  // EMI revise modal state
  const [reviseEmiOrderId, setReviseEmiOrderId] = useState(null);

  const fetchItems = async () => {
    try {
      const [quoteRes, advanceRes, emiRes] = await Promise.all([
        apiService.fetchQuotationActionItems(token, {
          page: quotationPage,
          limit: PAGE_SIZE,
        }),
        apiService.fetchAdvancePaymentActionItems(token, {
          page: advancePage,
          limit: PAGE_SIZE,
        }),
        apiService.fetchEmiActionItems(token, {
          page: emiPage,
          limit: PAGE_SIZE,
        }),
      ]);
      setQuotationItems(quoteRes.data || []);
      setQuotationPagination(quoteRes.pagination || null);
      setAdvanceItems(advanceRes.data || []);
      setAdvancePagination(advanceRes.pagination || null);
      setEmiItems(emiRes.data || []);
      setEmiPagination(emiRes.pagination || null);
    } catch (error) {
      toast.error("Failed to load action items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationPage, advancePage, emiPage]);

  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toISOString().split("T")[0];
  };

  // --- Advance payment link handlers ---
  const openAdvanceModal = (item) => {
    setSelectedAdvanceItem(item);
    setAdvanceForm({
      amount: "",
      dueDate: getDefaultDueDate(),
      paymentMode: "razorpay",
    });
  };
  const closeAdvanceModal = () => setSelectedAdvanceItem(null);

  const handleCreateLink = async () => {
    const amountValue = Number(advanceForm.amount);

    if (!amountValue || amountValue <= 0) {
      toast.error("Enter a valid advance amount");
      return;
    }
    if (advanceForm.dueDate === "") {
      toast.error("Due date is required");
      return;
    }
    if (advanceForm.paymentMode !== "razorpay") {
      toast.error("Only Razorpay link is supported right now");
      return;
    }
    setSubmittingAdvance(true);
    try {
      await apiService.createAdvancePaymentLink(
        selectedAdvanceItem.orderId,
        { amount: amountValue, dueDate: advanceForm.dueDate },
        token,
      );
      toast.success("Advance payment link created");
      closeAdvanceModal();
      fetchItems();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to create advance payment link",
      );
    } finally {
      setSubmittingAdvance(false);
    }
  };

  const handleResendFromCard = async (item) => {
    setResendingId(item.orderId);
    try {
      await apiService.resendAdvanceLink(item.orderId, token);
      toast.success("Payment link resent to customer");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to resend payment link",
      );
    } finally {
      setResendingId(null);
    }
  };

  // --- Quotation review handlers ---
  const openQuoteModal = (item) => {
    setSelectedQuoteItem(item);
    setNote("");
    setPricingChecked(false);
  };
  const closeQuoteModal = () => setSelectedQuoteItem(null);

  const handleSendQuotation = async () => {
    if (!pricingChecked) {
      toast.error("Confirm pricing, discount and GST before sending");
      return;
    }
    setSubmittingQuote(true);
    try {
      await apiService.sendQuotation(selectedQuoteItem.orderId, note, token);
      toast.success("Quotation sent to doctor");
      closeQuoteModal();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send quotation");
    } finally {
      setSubmittingQuote(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  const quotationTotal = quotationPagination?.total ?? quotationItems.length;
  const advanceTotal = advancePagination?.total ?? advanceItems.length;
  const emiTotal = emiPagination?.total ?? emiItems.length;
  const totalCount = quotationTotal + advanceTotal + emiTotal;

  const quotationRows = quotationItems.map((item) => ({
    key: `quote-${item.orderId}`,
    kind: "quotation",
    title: "Review and send quotation",
    tag: <Tag tone="s">Quotation</Tag>,
    meta: `${item.clinicName} · Generated ${timeAgo(item.createdAt)} · Order total: ${rupee(
      item.quote?.totalAmount,
    )}`,
    action: (
      <Box
        component="button"
        type="button"
        onClick={() => openQuoteModal(item)}
        sx={btnSx("ghost")}
      >
        Review
      </Box>
    ),
  }));

  const advanceRows = advanceItems.map((item) => {
    const isUnpaid = item.linkStatus === "link_created";
    const daysAgo = item.linkCreatedAt
      ? Math.floor(
          (Date.now() - new Date(item.linkCreatedAt).getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : null;

    return {
      key: `advance-${item.orderId}`,
      kind: "advance",
      title: isUnpaid
        ? "Follow up on unpaid advance"
        : "Create advance payment link",
      tag: <Tag tone={isUnpaid ? "b" : "bl"}>Advance</Tag>,
      meta: isUnpaid
        ? `${item.clinicName} · ${rupee(item.linkAmount)} · Sent ${daysAgo} days ago, not paid.`
        : `${item.clinicName} · Order total: ${rupee(item.quoteTotal)}`,
      action: (
        <Box
          component="button"
          type="button"
          onClick={() =>
            isUnpaid ? handleResendFromCard(item) : openAdvanceModal(item)
          }
          disabled={isUnpaid && resendingId === item.orderId}
          sx={btnSx("ghost")}
        >
          {isUnpaid ? "Resend link" : "Create link"}
        </Box>
      ),
    };
  });

  const emiRows = emiItems.map((item) => ({
    key: `emi-${item.orderId}`,
    kind: "emi",
    title: "Create revised payment terms",
    tag: <Tag tone="b">EMI</Tag>,
    meta: `${item.clinicName} · Order total: ${rupee(item.quoteTotal)}${
      item.rejectionReason ? ` · Rejected: ${item.rejectionReason}` : ""
    }`,
    action: (
      <Box
        component="button"
        type="button"
        onClick={() => setReviseEmiOrderId(item.orderId)}
        sx={btnSx("ghost")}
      >
        Revise
      </Box>
    ),
  }));

  const kpis = [
    { label: "Needs your action", value: totalCount, alarm: totalCount > 0 },
    { label: "Quotations to send", value: quotationTotal },
    { label: "Advance payments", value: advanceTotal },
    { label: "EMI to revise", value: emiTotal },
  ];

  /* One pager per source — these are logically distinct queues, not one
     combined list, so each gets its own page state (see fetchItems). */
  const Pager = ({ pagination, onPrev, onNext }) =>
    pagination && pagination.total > 0 ? (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          padding: "10px 2px 0",
          fontSize: 12,
          color: UI.ink3,
        }}
      >
        <span>
          {(pagination.page - 1) * pagination.limit + 1}–
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
          {pagination.total}
        </span>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Box
              component="button"
              type="button"
              onClick={onPrev}
              disabled={pagination.page <= 1}
              sx={btnSx("ghost", pagination.page <= 1)}
            >
              Previous
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onNext}
              disabled={pagination.page >= pagination.totalPages}
              sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
            >
              Next
            </Box>
          </Box>
        </Box>
      </Box>
    ) : null;

  const ActionGroup = ({ label, rows: groupRows, pagination, onPrev, onNext }) =>
    groupRows.length === 0 ? null : (
      <Box sx={{ marginBottom: "20px" }}>
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: UI.ink3,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            marginBottom: "6px",
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            bgcolor: UI.surface,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {groupRows.map((row, i) => (
            <ActionRow
              key={row.key}
              kind={row.kind}
              title={row.title}
              tag={row.tag}
              meta={row.meta}
              action={row.action}
              isLast={i === groupRows.length - 1}
            />
          ))}
        </Box>
        <Pager pagination={pagination} onPrev={onPrev} onNext={onNext} />
      </Box>
    );

  const dialogPaperSx = {
    bgcolor: UI.surface,
    backgroundImage: "none",
    border: "1px solid",
    borderColor: UI.line,
    borderRadius: "10px",
  };

  return (
    <Box
      sx={{
        padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
        maxWidth: 1240,
      }}
    >
      <PageHead
        title="Needs action"
        lede="Items waiting on you, grouped by what the order is blocked on."
        kpis={kpis}
      />

      <SectionTitle sub="Each item holds its order until you clear it.">
        Open items
      </SectionTitle>

      {totalCount === 0 ? (
        <Empty
          title="Nothing needs your action right now."
          sub="New items appear the moment an order needs your decision."
        />
      ) : (
        <Box sx={{ marginBottom: "8px" }}>
          <ActionGroup
            label="Quotations to send"
            rows={quotationRows}
            pagination={quotationPagination}
            onPrev={() => setQuotationPage((p) => Math.max(p - 1, 1))}
            onNext={() =>
              setQuotationPage((p) =>
                Math.min(p + 1, quotationPagination?.totalPages || p),
              )
            }
          />
          <ActionGroup
            label="Advance payments"
            rows={advanceRows}
            pagination={advancePagination}
            onPrev={() => setAdvancePage((p) => Math.max(p - 1, 1))}
            onNext={() =>
              setAdvancePage((p) =>
                Math.min(p + 1, advancePagination?.totalPages || p),
              )
            }
          />
          <ActionGroup
            label="EMI to revise"
            rows={emiRows}
            pagination={emiPagination}
            onPrev={() => setEmiPage((p) => Math.max(p - 1, 1))}
            onNext={() =>
              setEmiPage((p) => Math.min(p + 1, emiPagination?.totalPages || p))
            }
          />
        </Box>
      )}

      {/* QUOTATION REVIEW PANEL */}
      <Dialog
        open={Boolean(selectedQuoteItem)}
        onClose={closeQuoteModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        {selectedQuoteItem && (
          <React.Fragment>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                padding: "16px 18px",
                borderBottom: "1px solid",
                borderColor: UI.line,
              }}
            >
              <Box>
                <Box sx={{ fontSize: 16, fontWeight: 500, color: UI.ink }}>
                  Review and send quotation
                </Box>
                <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>
                  {selectedQuoteItem.clinicAddress ||
                    selectedQuoteItem.clinicName}
                </Box>
              </Box>
              <IconButton onClick={closeQuoteModal} size="small">
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "18px" }}>
              <Banner tone="i">
                Generated from the wizard. Sending emails the customer and
                notifies the Sales Lead.
              </Banner>

              <Box
                sx={{
                  border: "1px solid",
                  borderColor: UI.line,
                  borderRadius: "6px",
                  padding: "12px 14px",
                  marginBottom: "18px",
                }}
              >
                <Row
                  label="Product subtotal"
                  value={rupee(selectedQuoteItem.quote?.productSubtotal)}
                />
                <Row
                  label="Discount"
                  value={`-${rupee(selectedQuoteItem.quote?.discountAmount)}`}
                />
                {Number(selectedQuoteItem.quote?.engageSubtotal) > 0 && (
                  <React.Fragment>
                    <Row
                      label="Engage subscription"
                      value={rupee(selectedQuoteItem.quote?.engageSubtotal)}
                    />
                    <Row
                      label={`Engage discount${
                        selectedQuoteItem.quote?.engageDiscountPercent
                          ? `, ${selectedQuoteItem.quote.engageDiscountPercent}%`
                          : ""
                      }`}
                      value={`-${rupee(
                        selectedQuoteItem.quote?.engageDiscountAmount,
                      )}`}
                    />
                  </React.Fragment>
                )}
                {(selectedQuoteItem.quote?.engageAddOns || []).map(
                  (addOn, index) =>
                    Number(addOn.subtotal) > 0 ? (
                      <React.Fragment key={index}>
                        <Row
                          label={`Engage subscription ${index + 2}`}
                          value={rupee(addOn.subtotal)}
                        />
                        <Row
                          label={`Engage discount ${index + 2}${
                            addOn.discountPercent
                              ? `, ${addOn.discountPercent}%`
                              : ""
                          }`}
                          value={`-${rupee(addOn.discountAmount)}`}
                        />
                      </React.Fragment>
                    ) : null,
                )}
                {selectedQuoteItem.quote?.bagRequired && (
                  <Row
                    label="Carry bag"
                    value={rupee(selectedQuoteItem.quote?.bagSubtotal)}
                  />
                )}
                <Row
                  label="GST"
                  value={rupee(selectedQuoteItem.quote?.gstAmount)}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingTop: "9px",
                    marginTop: "5px",
                    borderTop: "1px solid",
                    borderTopColor: UI.line,
                    fontSize: 14,
                    fontWeight: 500,
                    color: UI.ink,
                  }}
                >
                  <span>Total</span>
                  <span>{rupee(selectedQuoteItem.quote?.totalAmount)}</span>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: "10px",
                  marginBottom: "18px",
                }}
              >
                <Stat
                  label="Payment terms"
                  value={selectedQuoteItem.quote?.paymentTerm || "—"}
                />
                <Stat
                  label="Place of supply"
                  value={selectedQuoteItem.placeOfSupply || "—"}
                />
              </Box>

              <TextField
                label="Note to customer, optional"
                multiline
                minRows={3}
                fullWidth
                size="small"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                sx={{ alignItems: "flex-start", marginLeft: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={pricingChecked}
                    onChange={(e) => setPricingChecked(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ paddingTop: "6px" }}>
                    <Box sx={{ fontSize: 13, color: UI.ink }}>
                      Pricing, discount and GST checked against the approved
                      price list
                    </Box>
                    <Box sx={{ fontSize: 11, color: UI.ink3 }}>
                      Sending locks the quotation.
                    </Box>
                  </Box>
                }
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "22px",
                  paddingTop: "16px",
                  borderTop: "1px solid",
                  borderTopColor: UI.line,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => setEditQuoteOrderId(selectedQuoteItem.orderId)}
                  sx={btnSx("ghost")}
                >
                  Edit
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSendQuotation}
                  disabled={submittingQuote}
                  sx={btnSx("solid", submittingQuote)}
                >
                  {submittingQuote ? "Sending" : "Send quotation"}
                </Box>
              </Box>
            </DialogContent>
          </React.Fragment>
        )}
      </Dialog>

      {/* ADVANCE PAYMENT LINK MODAL */}
      <Dialog
        open={Boolean(selectedAdvanceItem)}
        onClose={closeAdvanceModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        {selectedAdvanceItem && (
          <React.Fragment>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                padding: "16px 18px",
                borderBottom: "1px solid",
                borderColor: UI.line,
              }}
            >
              <Box>
                <Box sx={{ fontSize: 16, fontWeight: 500, color: UI.ink }}>
                  Create advance payment link
                </Box>
                <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>
                  {selectedAdvanceItem.clinicAddress ||
                    selectedAdvanceItem.clinicName}
                </Box>
              </Box>
              <IconButton onClick={closeAdvanceModal} size="small">
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "18px" }}>
              <Banner tone="g">Customer accepted the quotation.</Banner>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: "10px",
                  marginBottom: "18px",
                }}
              >
                <Stat
                  label="Payment term"
                  value={selectedAdvanceItem.paymentTerm || "—"}
                />
                <Stat
                  label="Order total"
                  value={rupee(selectedAdvanceItem.quoteTotal)}
                />
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "14px" }}
              >
                <TextField
                  label="Advance amount"
                  type="number"
                  size="small"
                  value={advanceForm.amount}
                  onChange={(e) =>
                    setAdvanceForm((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="Due date"
                  type="date"
                  size="small"
                  value={advanceForm.dueDate}
                  onChange={(e) =>
                    setAdvanceForm((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  select
                  label="Payment mode"
                  size="small"
                  value={advanceForm.paymentMode}
                  onChange={(e) =>
                    setAdvanceForm((prev) => ({
                      ...prev,
                      paymentMode: e.target.value,
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="razorpay">Razorpay link</MenuItem>
                  <MenuItem value="bank_transfer">
                    Bank transfer, manual reconciliation
                  </MenuItem>
                </TextField>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "22px",
                  paddingTop: "16px",
                  borderTop: "1px solid",
                  borderTopColor: UI.line,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={closeAdvanceModal}
                  sx={btnSx("flat")}
                >
                  Cancel
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCreateLink}
                  disabled={submittingAdvance}
                  sx={btnSx("solid", submittingAdvance)}
                >
                  {submittingAdvance ? "Creating" : "Create Razorpay link"}
                </Box>
              </Box>
            </DialogContent>
          </React.Fragment>
        )}
      </Dialog>

      <EditQuoteModal
        open={Boolean(editQuoteOrderId)}
        orderId={editQuoteOrderId}
        onClose={() => setEditQuoteOrderId(null)}
        onSaved={() => {
          setEditQuoteOrderId(null);
          closeQuoteModal();
          fetchItems();
        }}
      />

      <ReviseEmiTermsModal
        open={Boolean(reviseEmiOrderId)}
        orderId={reviseEmiOrderId}
        onClose={() => setReviseEmiOrderId(null)}
        onSaved={() => {
          setReviseEmiOrderId(null);
          fetchItems();
        }}
      />
    </Box>
  );
};

const Row = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      padding: "4px 0",
      fontSize: 13,
    }}
  >
    <Box component="span" sx={{ color: UI.ink3 }}>
      {label}
    </Box>
    <Box component="span" sx={{ color: UI.ink, fontWeight: 500 }}>
      {value}
    </Box>
  </Box>
);

export default NeedsAction;
